import { Lightbulb, X } from "lucide-react";
import { useTutorial } from "@/hooks/use-tutorial";
import { Button } from "@/components/ui/button";

interface TutorialTipProps {
  id: string;
  title: string;
  children: React.ReactNode;
  className?: string;
  side?: "bottom" | "top";
}

export function TutorialTip({ id, title, children, className = "" }: TutorialTipProps) {
  const { isTipVisible, dismissTip, disableTips } = useTutorial();

  if (!isTipVisible(id)) return null;

  return (
    <div
      className={`relative rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm animate-in fade-in slide-in-from-top-2 duration-300 ${className}`}
      data-testid={`tutorial-tip-${id}`}
    >
      <button
        onClick={() => dismissTip(id)}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`button-tip-close-${id}`}
        aria-label="Dismiss tip"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start gap-3 pr-5">
        <div className="mt-0.5 shrink-0 w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center">
          <Lightbulb className="w-4 h-4 text-primary" />
        </div>
        <div className="space-y-1">
          <p className="font-semibold text-foreground">{title}</p>
          <div className="text-muted-foreground leading-relaxed">{children}</div>
        </div>
      </div>

      <div className="mt-3 ml-10 flex items-center gap-4">
        <Button
          size="sm"
          variant="default"
          className="h-7 text-xs px-3"
          onClick={() => dismissTip(id)}
          data-testid={`button-got-it-${id}`}
        >
          Got it
        </Button>
        <button
          onClick={disableTips}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          data-testid={`button-turn-off-tips-${id}`}
        >
          Turn off all tips
        </button>
      </div>
    </div>
  );
}
